class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');

    // Business refresh (free shipping + shipping estimate) on open.
    if (typeof window.__refreshDrawerBusinessUI === 'function') window.__refreshDrawerBusinessUI();
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    // Business refresh after Dawn replaced drawer HTML.
    if (typeof window.__refreshDrawerBusinessUI === 'function') window.__refreshDrawerBusinessUI();

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

// --- Business layer (MR): shipping estimate + free-shipping progress ---
(function initCartDrawerBusinessLayer() {
  if (window.__MRCartDrawerBusinessInit__) return;
  window.__MRCartDrawerBusinessInit__ = true;

  window.__freeShippingUIState = window.__freeShippingUIState || {
    sliderVisible: false,
  };

  async function getGeoIpCountryHandle() {
    const key = '__geoip_country_handle__';
    const tsKey = '__geoip_country_handle_ts__';
    const now = Date.now();
    const ts = Number(localStorage.getItem(tsKey) || 0);
    const cached = localStorage.getItem(key);
    if (cached && ts && now - ts < 3600 * 1000) return cached;

    const r = await fetch('/browsing_context_suggestions.json', { credentials: 'same-origin' });
    const data = await r.json();
    const handle = String(data?.detected_values?.country?.handle || '').toUpperCase();
    if (handle) {
      localStorage.setItem(key, handle);
      localStorage.setItem(tsKey, String(now));
    }
    return handle;
  }

  function formatEurFrFromCents(cents) {
    return (cents / 100).toFixed(2).replace('.', ',') + ' \u20ac';
  }

  function setProgress(wrap, pct) {
    const fill = wrap?.querySelector('#FSPFill');
    const knob = wrap?.querySelector('#FSPKnob');
    if (!fill || !knob) return;

    const clamped = Math.max(0, Math.min(100, pct));
    fill.style.width = clamped + '%';
    knob.style.left = clamped + '%';
  }

  function hideProgressHard() {
    const wrap = document.querySelector('#CartDrawer #FreeShippingProgress');
    if (!wrap) return;
    wrap.classList.add('fsp--hidden');
    wrap.classList.remove('fs-updating');
    window.__freeShippingUIState.sliderVisible = false;
  }

  function showProgress() {
    const wrap = document.querySelector('#CartDrawer #FreeShippingProgress');
    if (!wrap) return;
    wrap.classList.remove('fsp--hidden');
    window.__freeShippingUIState.sliderVisible = true;
  }

  function setSliderUpdating(isUpdating) {
    const wrap = document.querySelector('#CartDrawer #FreeShippingProgress');
    if (!wrap) return;
    wrap.classList.toggle('fs-updating', Boolean(isUpdating));
  }

  async function updateFreeShippingSlider() {
    const wrap = document.querySelector('#CartDrawer #FreeShippingProgress');
    if (!wrap) return;

    const wasVisible = Boolean(window.__freeShippingUIState.sliderVisible);
    if (wasVisible) setSliderUpdating(true);

    let country = '';
    try {
      country = await getGeoIpCountryHandle();
    } catch (e) {
      // Keep previous state to avoid flicker.
      setSliderUpdating(false);
      return;
    }

    // PROD: only show for France (GeoIP).
    if (country !== 'FR') {
      hideProgressHard();
      return;
    }

    const lineEl = document.getElementById('DrawerShippingLine');
    const thresholdCents = Number(
      lineEl?.dataset?.frThreshold || window.__FREE_SHIPPING_FR_THRESHOLD_CENTS__ || 7900
    );
    const totalCents = Number(lineEl?.dataset?.totalCents || 0);

    if (!thresholdCents || thresholdCents <= 0) {
      hideProgressHard();
      return;
    }

    const remaining = thresholdCents - totalCents;
    const pct = thresholdCents > 0 ? (totalCents / thresholdCents) * 100 : 0;

    const text = wrap.querySelector('#FSPText');
    if (!text) {
      setSliderUpdating(false);
      return;
    }

    showProgress();

    if (remaining <= 0) {
      text.textContent = 'Livraison gratuite en France !';
      requestAnimationFrame(() => setProgress(wrap, 100));
    } else {
      text.textContent = `Plus que ${formatEurFrFromCents(Math.max(0, remaining))} pour profiter de la livraison offerte`;
      requestAnimationFrame(() => setProgress(wrap, pct));
    }

    setSliderUpdating(false);
  }

  window.__updateFreeShippingSlider = updateFreeShippingSlider;

  // One single refresh entrypoint (2 passes to avoid races with section rerenders).
  window.__refreshDrawerBusinessUI = function refreshDrawerBusinessUI() {
    const run = () => {
      if (typeof window.updateDrawerShippingEstimate === 'function') window.updateDrawerShippingEstimate();
      updateFreeShippingSlider();
    };

    setTimeout(run, 0);
    setTimeout(run, 120);
  };

  // Native hook: cart updates in Dawn go through PUB/SUB. Schedule refresh after updates.
  try {
    subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
      if (typeof window.__refreshDrawerBusinessUI === 'function') window.__refreshDrawerBusinessUI();
    });
  } catch (e) {
    // No-op: cart drawer still refreshes on open/renderContents.
  }

  // Avoid hidden-drawer work during initial page load; refresh runs on open and cart updates.
})();
